import { useMemo, useRef, useState } from 'react';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import type { WorkspaceSummary } from '@tabbio/types';
import { ChevronLeft } from 'lucide-react';
import { WebDialog } from '../common/WebDialog.web';
import { useAuth } from '../../hooks/useAuth';
import { useWorkspace } from '../../providers/useWorkspace';
import { CompanyFormFields } from '../company/CompanyFormFields.web';
import { fromWebFile, uploadImageAsset } from '../../lib/image-upload';
import {
  ensureCompanyWebsite,
  getCompanyFormSectionStatus,
  getCompanyFormStatus,
  type CompanyFormValues,
  toCompanySlug,
} from '../../lib/company-form';
import { completeOnboardingHiring } from '../../lib/onboarding-api';
import {
  mergeAuthenticatedUser,
  refreshAuthenticatedUser,
} from '../../lib/app-auth';
import { QUERY_KEYS } from '../../lib/query-keys';
import { trackProductEvent } from '../../lib/product-analytics';
import { useLocale } from '../../hooks/useLocale';

const COMPANY_SETUP_STEPS = ['identity', 'details', 'story'] as const;

const EMPTY_VALUES: CompanyFormValues = {
  name: '',
  slug: '',
  industry: '',
  employeeCount: '',
  location: '',
  website: '',
};

interface HiringCompanySetupProps {
  visible: boolean;
  onClose: () => void;
}

/**
 * Onboarding "Hiring?" escape hatch (employer Phase 1): lets a recruiter
 * create a company workspace and finish onboarding WITHOUT a CV, straight
 * from the ai-input screen. Mirrors `components/company/CreateCompanyScreen.web`
 * but opens as a dialog over onboarding (the `/create-company` route sits
 * behind the isOnboardingCompleted guard and is unreachable here).
 *
 * All errors render INLINE in the dialog footer, never as toasts.
 */
export function HiringCompanySetup({
  visible,
  onClose,
}: HiringCompanySetupProps) {
  const { t } = useTranslation('auth');
  const { isRTL } = useLocale();
  const router = useRouter();
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const { createCompanyWorkspace } = useWorkspace();

  const [values, setValues] = useState<CompanyFormValues>(EMPTY_VALUES);
  const [logoUrl, setLogoUrl] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [setupStepIndex, setSetupStepIndex] = useState(0);
  // Set when company creation succeeded but the completion call failed:
  // resubmitting must complete onboarding for THIS workspace, never create a
  // duplicate company.
  const [createdWorkspace, setCreatedWorkspace] =
    useState<WorkspaceSummary | null>(null);

  const formStatus = useMemo(() => getCompanyFormStatus(values), [values]);
  const sectionStatus = useMemo(
    () => getCompanyFormSectionStatus(values),
    [values],
  );
  const setupStep = COMPANY_SETUP_STEPS[setupStepIndex];
  const isLastSetupStep = setupStepIndex === COMPANY_SETUP_STEPS.length - 1;
  const canAdvance =
    sectionStatus[setupStep] && !isSubmitting && !isUploadingLogo;

  const stepCopy = [
    {
      title: t('onboarding.companyInfo.steps.identityTitle', {
        defaultValue: 'Create your company page',
      }),
      subtitle: t('onboarding.companyInfo.steps.identitySubtitle', {
        defaultValue: 'Start with the name candidates will recognize.',
      }),
    },
    {
      title: t('onboarding.companyInfo.steps.detailsTitle', {
        defaultValue: 'Tell us about your team',
      }),
      subtitle: t('onboarding.companyInfo.steps.detailsSubtitle', {
        defaultValue: 'These details help candidates understand your company.',
      }),
    },
    {
      title: t('onboarding.companyInfo.steps.storyTitle', {
        defaultValue: 'Bring your page to life',
      }),
      subtitle: t('onboarding.companyInfo.steps.storySubtitle', {
        defaultValue: 'Add your story now. You can refine everything later.',
      }),
    },
  ][setupStepIndex];

  const updateValues = (patch: Partial<CompanyFormValues>) => {
    setValues((previous) => {
      const next = { ...previous, ...patch };
      if (!slugTouched && patch.name !== undefined) {
        next.slug = toCompanySlug(patch.name);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!formStatus.isValid || isSubmitting || isUploadingLogo) return;

    setFormError(null);
    setIsSubmitting(true);
    try {
      // 1. Create the company workspace FIRST (unless a previous attempt
      //    already did): POST /api/workspaces/company creates the company,
      //    reserves the handle, adds the owner membership, and sets the
      //    active workspace server-side. Order matters: the employer layout
      //    bounces personal workspaces back to (app), so the company must
      //    exist before the completion flag flips.
      let workspace = createdWorkspace;
      if (!workspace) {
        const result = await createCompanyWorkspace({
          name: values.name.trim(),
          slug: formStatus.normalizedSlug,
          industry: values.industry.trim(),
          employeeCount: values.employeeCount.trim(),
          location: values.location.trim(),
          website: ensureCompanyWebsite(values.website),
          logo: logoUrl || undefined,
        });
        workspace = result.workspace;
        setCreatedWorkspace(result.workspace);
      }

      // 2. Complete onboarding on the hiring path (skips the starter CV and
      //    profile publish). One retry: the company already exists, so a
      //    transient failure here must not strand the user.
      try {
        await completeOnboardingHiring();
      } catch {
        await completeOnboardingHiring();
      }

      // 3+4. Mirror useCompleteOnboarding's cache work, then navigate in the
      // SAME tick the auth flag flips: otherwise Stack.Protected unmounts the
      // (onboarding) tree and app/index.tsx redirects before our replace
      // lands (same pattern OnboardingProvider's seeker completion uses).
      mergeAuthenticatedUser({ isOnboardingCompleted: true });
      void refreshAuthenticatedUser().catch(() => null);
      void queryClient.invalidateQueries({ queryKey: ['onboarding'] });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.WORKSPACES(user?.id),
      });
      router.replace('/(employer)/chat' as never);
      trackProductEvent('onboarding_hiring_company_created', {});
    } catch (error) {
      setIsSubmitting(false);
      setFormError(
        error instanceof Error && error.message
          ? error.message
          : t('onboarding.companyInfo.saveFailed', {
              defaultValue: 'Could not save company information',
            }),
      );
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setSetupStepIndex(0);
    setFormError(null);
    onClose();
  };

  const handleBack = () => {
    if (isSubmitting) return;
    if (setupStepIndex === 0) {
      handleClose();
      return;
    }
    setFormError(null);
    setSetupStepIndex((current) => current - 1);
  };

  const handlePrimaryAction = () => {
    if (!canAdvance) return;
    if (!isLastSetupStep) {
      setFormError(null);
      setSetupStepIndex((current) => current + 1);
      return;
    }
    void handleSubmit();
  };

  return (
    <WebDialog
      visible={visible}
      title={stepCopy.title}
      onClose={handleClose}
      size="large"
      hideHeader
      scrollable={false}
    >
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        style={{ display: 'none' }}
        onChange={(event) => {
          const file = event.target.files?.[0] ?? null;
          if (file) {
            void (async () => {
              const asset = fromWebFile(file);
              try {
                setIsUploadingLogo(true);
                const uploadedUrl = await uploadImageAsset(asset, {
                  kind: 'company-logo',
                  fileName: asset.name,
                  mimeType: asset.type,
                });
                setLogoUrl(uploadedUrl);
              } catch (error) {
                setFormError(
                  error instanceof Error && error.message
                    ? error.message
                    : t('onboarding.companyInfo.saveFailed', {
                        defaultValue: 'Could not save company information',
                      }),
                );
              } finally {
                URL.revokeObjectURL(asset.uri);
                setIsUploadingLogo(false);
              }
            })();
          }
          event.currentTarget.value = '';
        }}
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          background: 'var(--bg)',
        }}
      >
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: '32px 24px',
          }}
        >
          <div style={{ maxWidth: 560, marginInline: 'auto', width: '100%' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '36px 1fr 36px',
                alignItems: 'center',
                gap: 12,
                marginBottom: 56,
              }}
            >
              <button
                type="button"
                onClick={handleBack}
                aria-label={t('onboarding.back', { defaultValue: 'Back' })}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  padding: 0,
                  border: 0,
                  background: 'none',
                  cursor: 'pointer',
                }}
              >
                <ChevronLeft
                  size={24}
                  color="var(--text-primary)"
                  style={isRTL ? { transform: 'scaleX(-1)' } : undefined}
                />
              </button>
              <div
                aria-label={t('onboarding.companyInfo.steps.counter', {
                  current: setupStepIndex + 1,
                  total: COMPANY_SETUP_STEPS.length,
                  defaultValue: '{{current}} of {{total}}',
                })}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                {COMPANY_SETUP_STEPS.map((step, index) => (
                  <span
                    key={step}
                    style={{
                      height: 3,
                      flex: 1,
                      borderRadius: 999,
                      backgroundColor:
                        index <= setupStepIndex
                          ? 'var(--tabbio-button-primary-bg)'
                          : 'var(--gem-outline-variant)',
                    }}
                  />
                ))}
              </div>
              <span />
            </div>

            <h1
              style={{
                margin: '0 0 6px',
                color: 'var(--text-primary)',
                fontSize: 28,
                fontWeight: 600,
                letterSpacing: '-0.01em',
              }}
            >
              {stepCopy.title}
            </h1>
            <p
              style={{
                margin: '0 0 28px',
                color: 'var(--text-secondary)',
                fontSize: 15,
                lineHeight: '22px',
              }}
            >
              {stepCopy.subtitle}
            </p>

            <CompanyFormFields
              values={values}
              onChange={updateValues}
              onSlugTouched={() => setSlugTouched(true)}
              selectedLogo={logoUrl}
              onPickLogo={() => fileRef.current?.click()}
              section={setupStep}
            />
          </div>
        </div>

        <div
          style={{
            flexShrink: 0,
            borderTop: '1px solid var(--gem-outline-variant)',
            padding: '16px 24px max(20px, env(safe-area-inset-bottom))',
            background: 'var(--bg)',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              maxWidth: 560,
              marginInline: 'auto',
            }}
          >
            {formError ? (
              <p
                style={{
                  margin: 0,
                  color: 'var(--color-danger, #dc2626)',
                  fontSize: 13,
                  lineHeight: 1.4,
                  textAlign: 'center',
                }}
              >
                {formError}
              </p>
            ) : null}
            <button
              type="button"
              onClick={handlePrimaryAction}
              disabled={!canAdvance}
              style={{
                width: '100%',
                height: 54,
                borderRadius: 9999,
                border: canAdvance
                  ? 'none'
                  : '1px solid var(--gem-outline-variant)',
                cursor: canAdvance ? 'pointer' : 'default',
                backgroundColor: canAdvance
                  ? 'var(--tabbio-button-primary-bg)'
                  : 'var(--alpha-05)',
                color: canAdvance
                  ? 'var(--tabbio-button-primary-text)'
                  : 'var(--text-tertiary)',
                fontSize: 16,
                fontWeight: 600,
                fontFamily: 'var(--font-sans)',
                opacity: canAdvance ? 1 : 0.7,
              }}
            >
              {isSubmitting
                ? t('onboarding.companyInfo.saving', {
                    defaultValue: 'Saving...',
                  })
                : isLastSetupStep
                  ? t('onboarding.companyInfo.createPage', {
                      defaultValue: 'Create company page',
                    })
                  : t('onboarding.next', { defaultValue: 'Next' })}
            </button>
          </div>
        </div>
      </div>
    </WebDialog>
  );
}
